import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, X, MapPin, Target, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscoverPage = () => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [revealedPhotos, setRevealedPhotos] = useState(new Set());

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/matches/discover`);
      setProfiles(response.data);
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!profiles[currentIndex]) return;
    setActionLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/matches/${profiles[currentIndex].id}/like`);
      
      if (response.data.is_match) {
        toast.success("It's a match! You can now chat.");
      } else {
        toast.success('Like sent!');
      }
      
      nextProfile();
    } catch (error) {
      console.error('Failed to like:', error);
      toast.error('Failed to send like');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePass = () => {
    nextProfile();
  };

  const nextProfile = () => {
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const togglePhotoReveal = (profileId) => {
    setRevealedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(profileId)) {
        newSet.delete(profileId);
      } else {
        newSet.add(profileId);
      }
      return newSet;
    });
  };

  const currentProfile = profiles[currentIndex];
  const isPhotoRevealed = currentProfile ? revealedPhotos.has(currentProfile.id) : false;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="discover-page">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold mb-2">Discover</h1>
          <p className="text-muted-foreground">Find your compatible match</p>
        </div>

        {!currentProfile || currentIndex >= profiles.length ? (
          <Card className="rounded-2xl border-border/50 shadow-soft">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-serif font-medium mb-2">No more profiles</h3>
              <p className="text-muted-foreground mb-6">
                Check back later for new matches or complete more quizzes to improve your compatibility score.
              </p>
              <Button onClick={fetchProfiles} className="rounded-full">
                Refresh
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl border-border/50 shadow-float overflow-hidden" data-testid="profile-card">
            {/* Profile Photo */}
            <div className="relative aspect-[4/5] bg-muted">
              {currentProfile.profile_photo_url ? (
                <div 
                  className="relative w-full h-full cursor-pointer"
                  onClick={() => togglePhotoReveal(currentProfile.id)}
                >
                  <img 
                    src={currentProfile.profile_photo_url}
                    alt={currentProfile.display_name}
                    className={`w-full h-full object-cover transition-all duration-500 ${
                      isPhotoRevealed ? '' : 'blur-xl'
                    }`}
                  />
                  {!isPhotoRevealed && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white bg-black/30 backdrop-blur-sm rounded-2xl p-6">
                        <p className="font-medium mb-1">Photo is blurred for privacy</p>
                        <p className="text-sm opacity-80">Tap to reveal temporarily</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Avatar className="w-32 h-32">
                    <AvatarFallback className="text-4xl bg-primary/10 text-primary">
                      {currentProfile.display_name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              )}

              {/* Compatibility Badge */}
              <div className="absolute top-4 right-4">
                <Badge className="bg-white/90 text-foreground rounded-full px-3 py-1 shadow-soft">
                  {Math.round(currentProfile.compatibility_score)}% Match
                </Badge>
              </div>
            </div>

            {/* Profile Info */}
            <CardContent className="p-6">
              <div className="mb-4">
                <h2 className="text-2xl font-serif font-semibold mb-1">
                  {currentProfile.display_name}
                </h2>
                
                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                  {currentProfile.location_city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {currentProfile.location_city}, {currentProfile.location_state}
                    </span>
                  )}
                  {currentProfile.relationship_goal && (
                    <span className="flex items-center gap-1">
                      <Target className="w-4 h-4" />
                      {currentProfile.relationship_goal.replace('_', ' ')}
                    </span>
                  )}
                </div>
              </div>

              {currentProfile.bio && (
                <p className="text-muted-foreground mb-4 line-clamp-3">
                  {currentProfile.bio}
                </p>
              )}

              {/* Quiz Completion */}
              {currentProfile.quiz_scores && Object.keys(currentProfile.quiz_scores).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  <Badge variant="secondary" className="rounded-full">
                    {Object.keys(currentProfile.quiz_scores).length} quizzes completed
                  </Badge>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-16 h-16 rounded-full p-0 hover:bg-destructive/10 hover:border-destructive/30"
                  onClick={handlePass}
                  disabled={actionLoading}
                  data-testid="pass-btn"
                >
                  <X className="w-7 h-7 text-muted-foreground" />
                </Button>
                
                <Button
                  size="lg"
                  className="w-20 h-20 rounded-full p-0 bg-primary hover:bg-primary/90"
                  onClick={handleLike}
                  disabled={actionLoading}
                  data-testid="like-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="w-8 h-8 animate-spin" />
                  ) : (
                    <Heart className="w-8 h-8" fill="currentColor" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Remaining count */}
        {profiles.length > 0 && currentIndex < profiles.length && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            {profiles.length - currentIndex} profiles remaining
          </p>
        )}
      </div>
    </Layout>
  );
};

export default DiscoverPage;
